import { ApiProject } from "@incendium/api";
import {
  Box,
  ListSubheader,
  Menu,
  MenuItem,
  Paper,
  Typography,
  styled,
} from "@mui/material";
import { KeyboardArrowDown as KeyboardArrowDownIcon } from "@mui/icons-material";
import { useSelectedProject } from "Hooks";
import React, { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import useAllProjects from "features/project/hooks/useAllProjects";
import { useMount } from "react-use";
import { useDispatch, useSelector } from "react-redux";
import { initializeClientsThunk } from "Actions";
import { clientListSelector } from "Selectors/clientListSelector";

const DropdownPaper = styled(Paper)({
  minWidth: 260,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
});

const ProjectTrigger = styled(Box)(({ theme }) => ({
  display: "flex",
  cursor: "pointer",
  alignItems: "center",
  padding: "3px 12px 3px 16px",
  minHeight: 36,
  borderRadius: theme.shape.borderRadius,
  border: "1px solid #37345d",
  color: theme.palette.common.white,
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.08)",
  },
}));

const IconDown = styled(Box, {
  shouldForwardProp: (prop) => prop !== "open",
})<{ open?: boolean }>(({ theme, open }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  color: theme.palette.common.white,
  marginLeft: 4,
  "& svg": {
    transition: "all 0.2s",
    ...(open && { transform: "rotate(180deg)" }),
  },
}));

const StyledListSubheader = styled(ListSubheader)({
  lineHeight: 1.5,
  paddingTop: 8,
  paddingBottom: 4,
  "&:not(:first-of-type)": {
    paddingTop: 16,
  },
});

function ProjectClientSelector() {
  const [isMenuOpen, setIsMenuOpen] = useState<Element | null>(null);
  const history = useHistory();
  const dispatch = useDispatch();
  const { selectedProject } = useSelectedProject();
  const { allProjects, fetched } = useAllProjects();
  const clientsState = useSelector(clientListSelector);

  const projectsByClient = useMemo(() => {
    const projects = allProjects ?? [];
    const clientMap = new Map(
      (clientsState?.list ?? []).map((c) => [c.id, c.businessName ?? ""]),
    );
    const byClient = new Map<number, ApiProject[]>();
    for (const p of projects) {
      const cid = p.clientId;
      if (cid == null) continue;
      if (!byClient.has(cid)) byClient.set(cid, []);
      byClient.get(cid)!.push(p);
    }
    const clientNames = Array.from(byClient.keys()).map((id) => ({
      clientId: id,
      name: clientMap.get(id) ?? "",
    }));
    clientNames.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );
    return clientNames.map(({ clientId }) => ({
      clientId,
      clientName: clientMap.get(clientId) ?? "",
      projects: (byClient.get(clientId) ?? []).sort((a, b) =>
        (a.name ?? "").localeCompare(b.name ?? "", undefined, {
          sensitivity: "base",
        }),
      ),
    }));
  }, [allProjects, clientsState?.list]);

  useMount(() => {
    dispatch(initializeClientsThunk());
  });

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (isMenuOpen) {
        return setIsMenuOpen(null);
      }
      setIsMenuOpen(e.currentTarget);
    },
    [isMenuOpen],
  );

  const handleClose = useCallback(() => {
    setIsMenuOpen(null);
  }, []);

  const handleProjectClick = useCallback(
    (project: ApiProject) => () => {
      handleClose();
      if (project.clientId != null && project.id != null) {
        history.push(`/clients/${project.clientId}/projects/${project.id}`);
      }
    },
    [handleClose, history],
  );

  const displayName = selectedProject?.name ?? "Select Project";

  if (!fetched) {
    return null;
  }
  if (projectsByClient.length === 0) {
    return null;
  }

  if ((allProjects || []).length <= 1) {
    return null;
  }

  return (
    <>
      <ProjectTrigger onClick={handleClick}>
        <Typography variant="subtitle1" color="inherit" noWrap fontWeight={600}>
          {displayName}
        </Typography>
        <IconDown open={Boolean(isMenuOpen)}>
          <KeyboardArrowDownIcon />
        </IconDown>
      </ProjectTrigger>
      <Menu
        anchorEl={isMenuOpen}
        PaperProps={{
          component: DropdownPaper,
          variant: "outlined",
        }}
        open={Boolean(isMenuOpen)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        onClose={handleClose}
      >
        <MenuItem divider disabled>
          <Typography variant="subtitle2" color={"inherit"} gutterBottom>
            Select Project
          </Typography>
        </MenuItem>
        {projectsByClient.map(({ clientId, clientName, projects }) => (
          <React.Fragment key={`client-${clientId}`}>
            <StyledListSubheader disableSticky>
              {clientName}
            </StyledListSubheader>
            {projects.map((project) => (
              <MenuItem
                key={`project-${project.id}`}
                onClick={handleProjectClick(project)}
              >
                <Typography color="inherit">{project.name}</Typography>
              </MenuItem>
            ))}
          </React.Fragment>
        ))}
      </Menu>
    </>
  );
}

export default ProjectClientSelector;
